import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, throwError } from 'rxjs';
import { map, catchError, switchMap, tap, filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  VideoSplitRequest,
  VideoSplitResponse,
  VisualSplitResult,
  ProgressUpdate,
  VisualSplitError,
  VisualSplitErrorType
} from '../models/visual-split.model';
import { FireflyService } from './firefly.service';
import { ToastService } from './toast.service';
import { LogService } from './log.service';
import { VideoSettings, VIDEO_SIZES } from '../models/video-settings.model';
import { I18nService } from '../i18n/i18n.service';

@Injectable({
  providedIn: 'root'
})
export class VisualSplitService {
  private apiUrl = `${environment.apiUrl}`;

  // 進行状況の管理
  private progressSubject!: BehaviorSubject<ProgressUpdate>;

  // エラー通知
  private errorSubject = new Subject<VisualSplitError>();

  constructor(
    private http: HttpClient,
    private fireflyService: FireflyService,
    private toastService: ToastService,
    private logService: LogService,
    private i18n: I18nService
  ) {
    this.progressSubject = new BehaviorSubject<ProgressUpdate>({
      currentStep: -1,
      steps: [
        { name: this.i18n.t('kvs.stepGenerate'), status: 'pending', percentage: 0 },
        { name: this.i18n.t('kvs.stepSplit'), status: 'pending', percentage: 0 }
      ],
      isComplete: false
    });
  }

  /**
   * 進行状況の監視
   */
  get progress$(): Observable<ProgressUpdate> {
    return this.progressSubject.asObservable();
  }

  /**
   * エラーの監視
   */
  get error$(): Observable<VisualSplitError> {
    return this.errorSubject.asObservable();
  }

  /**
   * メイン処理：画像から動画を生成し、指定数に分割
   */
  processImageToSplitFrames(imageFile: File, prompt: string, splitCount: number, videoSettings?: VideoSettings): Observable<VisualSplitResult> {
    this.resetProgress();

    return this.generateVideo(imageFile, prompt, videoSettings).pipe(
      switchMap(videoResult =>
        this.splitVideo(videoResult.videoUrl, splitCount).pipe(
          map(splitResult => ({
            success: true,
            images: splitResult.data?.images || [],
            totalCount: splitResult.data?.totalFrames || 0,
            videoUrl: videoResult.videoUrl // 生成された動画URLを保持
          }))
        )
      ),
      tap(result => {
        this.updateProgress(1, 'completed', 100);
        this.completeProgress(result);
      }),
      catchError(error => {
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * ステップ1: 動画生成
   */
  private generateVideo(imageFile: File, prompt: string, videoSettings?: VideoSettings): Observable<{ videoUrl: string }> {
    this.updateProgress(0, 'active', 0);

    // 画像をアップロードしてから動画生成
    return this.fireflyService.uploadFile(imageFile).pipe(
      switchMap(uploadResponse => {
        if (!uploadResponse.success || !(uploadResponse.uploadId || uploadResponse.fileId)) {
          throw new Error(this.i18n.t('kvs.imageUploadFailed'));
        }

        const uploadId = uploadResponse.uploadId || uploadResponse.fileId;

        // サイズ設定を取得
        const selectedSize = videoSettings?.selectedSize ? VIDEO_SIZES[videoSettings.selectedSize] : VIDEO_SIZES['540p'];

        // Firefly Video API v3のリクエスト形式
        const requestBody: any = {
          prompt: prompt.trim(),
          localeCode: "ja_JP",
          sizes: [
            {
              height: selectedSize.height,
              width: selectedSize.width
            }
          ],
          output: {
            storeInputs: false
          },
          image: {
            conditions: [
              {
                source: {
                  uploadId: uploadId
                },
                placement: {
                  position: 0
                }
              }
            ]
          }
        };

        // videoSettingsがある場合は追加
        if (videoSettings) {
          const videoSettingsPayload: any = {};
          if (videoSettings.cameraMotion) {
            videoSettingsPayload.camera_motion = videoSettings.cameraMotion;
          }
          if (videoSettings.promptStyle) {
            videoSettingsPayload.prompt_style = videoSettings.promptStyle;
          }
          if (videoSettings.shotAngle) {
            videoSettingsPayload.shot_angle = videoSettings.shotAngle;
          }
          if (videoSettings.shotSize) {
            videoSettingsPayload.shot_size = videoSettings.shotSize;
          }

          if (Object.keys(videoSettingsPayload).length > 0) {
            requestBody.videoSettings = videoSettingsPayload;
          }
        }

        return this.fireflyService.generateVideoAsyncV3(requestBody);
      }),
      tap(response => {
        this.toastService.showInfo(this.i18n.t('kvs.apiChecking'));
        console.log('Firefly動画生成API レスポンス:', response);

        if (response?.success && (response.jobId || response.data?.jobId)) {
          this.logService.logGenerateContent(
            { 
              tool: 'key_frame_generator',
              contentType: 'video'
            }
          ).subscribe();
        }
      }),
      switchMap(response => {
        // レスポンス構造を詳細にログ出力
        console.log('レスポンス構造:', JSON.stringify(response, null, 2));

        if (response.success && response.jobId) {
          this.toastService.showInfo(this.i18n.t('kvs.videoJobStarted'));
          return this.fireflyService.pollJobStatus(response.jobId);
        } else if (response.success && response.data?.jobId) {
          this.toastService.showInfo(this.i18n.t('kvs.videoJobStarted'));
          return this.fireflyService.pollJobStatus(response.data.jobId);
        }

        this.toastService.showError(this.i18n.t('kvs.videoJobStartFailed'));
        console.error('動画生成ジョブ開始失敗:', response);
        throw new Error(this.i18n.t('kvs.videoJobStartFailed'));
      }),
      // ポーリング結果を処理 - takeWhile(condition, true)により最終結果も含まれる
      tap(response => {
        console.log('ポーリングレスポンス:', response);

        // 'running'/'pending'ステータスは進行中の表示のみ
        if (response.status === 'running' || response.status === 'pending') {
          this.updateProgress(0, 'active', response.progress || 50);
          this.toastService.showInfo(this.i18n.t('kvs.generatingVideo', { status: response.status }));
        }
      }),
      // 最終的な成功/失敗のみを処理
      filter(response => response.status === 'succeeded' || response.status === 'failed'),
      map(response => {
        if (response.status === 'succeeded') {
          // video-branch-generatorと同じ動画URL抽出パターン
          let videoUrl: string | null = null;

          if (response.result && response.result.outputs && response.result.outputs.length > 0) {
            const videoOutput = response.result.outputs[0];
            if (videoOutput.video && videoOutput.video.url) {
              videoUrl = videoOutput.video.url;
            }
          }

          if (videoUrl) {
            this.updateProgress(0, 'completed', 100);
            this.toastService.showSuccess(this.i18n.t('kvs.videoGenComplete'));
            return { videoUrl };
          } else {
            this.toastService.showError(this.i18n.t('kvs.videoUrlFailed'));
            throw new Error(this.i18n.t('kvs.videoUrlFailed'));
          }
        } else if (response.status === 'failed') {
          this.toastService.showError(this.i18n.t('kvs.videoGenFailed'));
          throw new Error(this.i18n.t('kvs.videoGenFailed'));
        }

        // ここには到達しないはず
        throw new Error(`予期しないステータス: ${response.status}`);
      }),
      catchError(error => {
        this.emitError(VisualSplitErrorType.VIDEO_GENERATION_ERROR, this.i18n.t('kvs.videoGenError'), error);
        return throwError(() => error);
      })
    );
  }

  /**
   * ステップ2: 動画分割
   */
  private splitVideo(videoUrl: string, splitCount: number): Observable<VideoSplitResponse> {
    this.updateProgress(1, 'active', 0);

    const splitRequest: VideoSplitRequest = {
      videoUrl,
      splitCount
    };

    return this.http.post<VideoSplitResponse>(`${this.apiUrl}/video/split`, splitRequest).pipe(
      tap(() => this.updateProgress(1, 'active', 100)),
      catchError(error => {
        this.emitError(VisualSplitErrorType.VIDEO_SPLIT_ERROR, this.i18n.t('kvs.videoSplitError'), error);
        return throwError(() => error);
      })
    );
  }

  /**
   * 進行状況の更新
   */
  private updateProgress(stepIndex: number, status: 'pending' | 'active' | 'completed', percentage: number): void {
    const currentProgress = this.progressSubject.value;
    const steps = [...currentProgress.steps];

    if (steps[stepIndex]) {
      steps[stepIndex] = { ...steps[stepIndex], status, percentage };
    }

    this.progressSubject.next({
      ...currentProgress,
      currentStep: stepIndex,
      steps
    });
  }

  /**
   * 処理完了
   */
  private completeProgress(result: VisualSplitResult): void {
    const currentProgress = this.progressSubject.value;
    this.progressSubject.next({
      ...currentProgress,
      isComplete: true,
      result
    });
  }

  /**
   * 進行状況のリセット
   */
  private resetProgress(): void {
    this.progressSubject.next({
      currentStep: -1,
      steps: [
        { name: this.i18n.t('kvs.stepGenerate'), status: 'pending', percentage: 0 },
        { name: this.i18n.t('kvs.stepSplit'), status: 'pending', percentage: 0 }
      ],
      isComplete: false
    });
  }

  /**
   * エラーの発行
   */
  private emitError(type: VisualSplitErrorType, message: string, details?: any): void {
    const error: VisualSplitError = { type, message, details };
    this.errorSubject.next(error);
    this.toastService.showError(message);
  }

  /**
   * エラーハンドリング
   */
  private handleError(error: any): void {
    console.error('VisualSplitService Error:', error);

    let errorType = VisualSplitErrorType.UNKNOWN_ERROR;
    let message = this.i18n.t('kvs.generalError');

    if (error.status === 0) {
      errorType = VisualSplitErrorType.NETWORK_ERROR;
      message = this.i18n.t('kvs.networkError');
    } else if (error.status >= 400 && error.status < 500) {
      message = this.i18n.t('kvs.requestError');
    } else if (error.status >= 500) {
      message = this.i18n.t('kvs.serverError');
    }

    this.emitError(errorType, message, error);
  }

  /**
   * 動画のみを分割（再分割用）
   */
  splitVideoOnly(videoUrl: string, splitCount: number): Observable<VideoSplitResponse> {
    const splitRequest: VideoSplitRequest = {
      videoUrl,
      splitCount
    };

    return this.http.post<VideoSplitResponse>(`${this.apiUrl}/video/split`, splitRequest).pipe(
      tap(() => {
        this.toastService.showSuccess(this.i18n.t('kvs.resplitSuccess', { count: String(splitCount) }));
      }),
      catchError(error => {
        this.toastService.showError(this.i18n.t('kvs.resplitFailed'));
        return throwError(() => error);
      })
    );
  }

}
